// Tabella + paginazione della console Invio Pratiche (estratto move-only, Fase 5).
import React from "react";
import { Send } from "lucide-react";
import {
  STATI_CONCLUSI, FORNITORE_BADGE, statoLabel, statoBadgeCls, fmtData,
} from "./invioPratiche.shared";
import type { UnifiedRow } from "./invioPratiche.shared";

// Lo stato dell'utenza si legge dalla parola, non dal solo colore: solo gli stati
// che fermano l'invio hanno un badge (l'utenza attiva non ha nulla da segnalare).
// Le credenziali mai inserite non sono un guasto: tono neutro, non rosso.
const UTENZA_BADGE: Record<string, { label: string; cls: string }> = {
  da_ripristinare: { label: "da ripristinare", cls: "bg-red-100 text-red-800 border border-red-300" },
  sessione_scaduta: { label: "sessione scaduta", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
  non_assegnata: { label: "non assegnata", cls: "bg-red-100 text-red-800 border border-red-300" },
  da_configurare: { label: "credenziali da inserire", cls: "bg-muted text-muted-foreground border border-border" },
};

// L'utenza fornitore è un indirizzo email il cui dominio è sempre quello del portale
// del fornitore (`@crm.a2aenergia.it`): ripetuto su ogni riga costa ~130px di colonna e
// non distingue nulla. In lista si mostra la parte che identifica l'utenza, il valore
// intero resta nel title.
function utenzaBreve(username: string): string {
  const at = username.indexOf("@");
  return at > 0 ? username.slice(0, at) : username;
}

export interface InvioPraticheTabellaProps {
  rows: UnifiedRow[]; loading: boolean;
  order: "asc" | "desc";
  setOrder: React.Dispatch<React.SetStateAction<"asc" | "desc">>;
  offset: number;
  setOffset: React.Dispatch<React.SetStateAction<number>>;
  total: number; limit: number;
  fornitoreFilter: "" | "A2A" | "Sentra" | "Iren";
  onOpen: (row: UnifiedRow) => void;
}

export function InvioPraticheTabella(props: InvioPraticheTabellaProps) {
  const { rows, loading, order, setOrder, offset, setOffset, total, limit, fornitoreFilter, onOpen } = props;
  return (
    <>
      {/* Tabella */}
      {loading ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessuna pratica da inviare per i filtri selezionati.</p>
      ) : (
        <>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left py-2.5 px-3 font-medium">Cliente</th>
                <th className="text-left py-2.5 px-3 font-medium">Fornitore</th>
                <th className="text-left py-2.5 px-3 font-medium">Offerta</th>
                <th
                  className="text-left py-2.5 px-3 font-medium cursor-pointer select-none"
                  aria-sort={order === "desc" ? "descending" : "ascending"}
                  onClick={() => setOrder((o) => o === "desc" ? "asc" : "desc")}
                >
                  Data {order === "desc" ? "▾" : "▴"}
                </th>
                <th className="text-left py-2.5 px-3 font-medium">Stato</th>
                <th className="text-left py-2.5 px-3 font-medium">Rif. esterno</th>
                <th className="text-right py-2.5 px-3 font-medium">Azione</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const rifEsterno = r.fornitore === "A2A" ? r.a2a_opp_id
                  : r.fornitore === "Sentra" ? r.contratto_id
                  : r.iren_pratica_id;
                const moduliMancanti = r.moduli_mancanti ?? [];
                const utenzaBadge = UTENZA_BADGE[r.utenza_stato ?? ""];
                return (
                <tr key={`${r.fornitore}-${r.opp_id}`} className="border-t border-border align-top">
                  {/* Il tipo di pratica sta sotto il nome: sono lo stesso dato ("chi" e
                      "che pratica"), e due colonne separate non ci stavano più in pagina. */}
                  <td className="py-2.5 px-3">
                    <span className="block text-foreground font-medium">{r.cliente}</span>
                    {r.tipo && <span className="block text-xs text-muted-foreground">{r.tipo}</span>}
                  </td>
                  {/* Fornitore + utenza fornitore: l'utenza è "con quale accesso di quel
                      fornitore", concetto per ora solo A2A (Sentra/Iren hanno un'unica
                      utenza di casa, il backend non la espone sulla riga). */}
                  <td className="py-2.5 px-3">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full ${FORNITORE_BADGE[r.fornitore]}`}>
                        {r.fornitore}
                      </span>
                      {/* Un identificativo non si spezza a metà parola: `break-all` rendeva
                          «nexthomegroups_1» come «nexthomegroups_» + «1» su ogni riga. Se non
                          ci sta si tronca coi puntini, e il nome intero resta nel title. */}
                      {r.fornitore === "A2A" && r.utenza_username && (
                        <span className="block max-w-[140px] truncate text-xs text-muted-foreground"
                              title={r.utenza_username}>
                          {utenzaBreve(r.utenza_username)}
                        </span>
                      )}
                      {r.fornitore === "A2A" && utenzaBadge && (
                        <span className={`inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full ${utenzaBadge.cls}`}>
                          {utenzaBadge.label}
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Offerta + commodity: la commodity è già implicita nell'offerta e i nomi
                      offerta arrivano a 41 caratteri → tetto di larghezza e due righe, il
                      nome intero nel title. */}
                  <td className="py-2.5 px-3">
                    {/* niente `block`: sovrascrive il display:-webkit-box di line-clamp e il
                        tetto di due righe non scatta più (visto a 390px, 5 righe). */}
                    <span className="max-w-[220px] break-words line-clamp-2 text-muted-foreground" title={r.offerta || undefined}>
                      {r.offerta || "—"}
                    </span>
                    {r.commodity && <span className="block text-xs text-muted-foreground">{r.commodity}</span>}
                  </td>
                  <td className="py-2.5 px-3 text-muted-foreground tabular-nums whitespace-nowrap">
                    {fmtData(r.created_at).split(" ").map((p, i) => (
                      <span key={i} className={i === 0 ? "block" : "block text-xs"}>{p}</span>
                    ))}
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full ${statoBadgeCls(r)}`}>
                        {statoLabel(r)}
                      </span>
                      {/* badge moduli mancanti: concetto solo A2A (Sentra non ha moduli firmati) */}
                      {moduliMancanti.length > 0 && (r.stato === "da_inviare" || r.stato === "errore" || r.stato === "bozza") && (
                        <span title={`Mancano: ${moduliMancanti.join(", ")}`}
                              className="inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                          Moduli mancanti
                        </span>
                      )}
                      {r.motivo_non_inviabile && (
                        <span title={r.motivo_non_inviabile}
                              className="inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300">
                          Non inviabile
                        </span>
                      )}
                      {r.motivo_annullamento && (
                        <span className="text-xs text-muted-foreground">
                          Motivo: <span className="text-rose-700">{r.motivo_annullamento}</span>
                        </span>
                      )}
                      {r.motivo_errore && (
                        <span className="text-xs text-muted-foreground block max-w-[260px] break-words line-clamp-2" title={r.motivo_errore}>
                          Errore: <span className="text-red-700">{r.motivo_errore}</span>
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Rif. esterno: su A2A i codici sono due. Sopra il numero pratica (OP…),
                      che è quello che si detta all'assistenza; sotto, come le altre colonne
                      fanno col dato secondario, l'identificativo interno del portale, che
                      serve ad aprire la pratica ma non si detta. Il numero arriva col primo
                      giro di polling dopo l'invio: finché manca resta solo l'identificativo. */}
                  <td className="py-2.5 px-3">
                    {rifEsterno ? (
                      <div className="flex flex-col items-start">
                        {r.a2a_numero_pratica && (
                          <span className="font-mono text-xs text-foreground">{r.a2a_numero_pratica}</span>
                        )}
                        {/* niente break-all: l'identificativo si copia intero, e spezzato
                            su tre righe alzava la riga della tabella. */}
                        <span className={`font-mono text-xs whitespace-nowrap ${
                          r.a2a_numero_pratica ? "text-muted-foreground" : "text-foreground"}`}>
                          {rifEsterno}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <button
                      type="button"
                      onClick={() => onOpen(r)}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1 text-xs border border-border rounded-lg hover:bg-muted transition-colors cursor-pointer"
                    >
                      {r.stato === "inviata" || r.stato === "attesa_firma" || r.stato === "firmata"
                        || STATI_CONCLUSI.has(r.stato) || r.motivo_non_inviabile ? (
                        "Dettaglio"
                      ) : (
                        <>
                          <Send size={13} className="text-emerald-600" />
                          Verifica e invia
                        </>
                      )}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
          <span>{
            total === 0 ? "0"
              // Tutti: due sorgenti paginate separatamente → il range "1–25" mentirebbe
              // (si renderizzano fino a 2×LIMIT righe). Mostra righe viste / totale combinato.
              : fornitoreFilter === "" ? `${rows.length} righe · ${total} totali`
              : `${offset + 1}–${Math.min(offset + limit, total)} di ${total}`
          }</span>
          <div className="flex gap-2">
            <button type="button" disabled={offset === 0}
              onClick={() => setOffset(o => Math.max(0, o - limit))}
              className="px-3 py-1 border border-border rounded-lg disabled:opacity-40 cursor-pointer">Prec</button>
            <button type="button" disabled={offset + limit >= total}
              onClick={() => setOffset(o => o + limit)}
              className="px-3 py-1 border border-border rounded-lg disabled:opacity-40 cursor-pointer">Succ</button>
          </div>
        </div>
        </>
      )}
    </>
  );
}
